// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ==========================================================================
   WIZZY design system — premium light theme with ink + brand green
   Purely visual: no layout/behavioral overrides beyond spacing & skin.
   ========================================================================== */

:root {
  /* Brand */
  --wz-green: #b6ce3b;
  --wz-green-strong: #a4bd2d;
  --wz-green-deep: #5f7d16;      /* readable green for text on light */
  --wz-green-tint: #f3f7e2;      /* subtle green wash */
  --wz-green-tint-2: #e9f0cd;    /* stronger wash (active states) */
  --wz-green-glow: rgba(182, 206, 59, 0.32);

  /* Ink & text */
  --wz-ink: #12160f;             /* near-black with green undertone */
  --wz-ink-2: #232920;
  --wz-text: #1c211a;
  --wz-text-2: #5d6457;
  --wz-text-3: #8f968a;

  /* Surfaces */
  --wz-bg: #f6f7f1;              /* app background (warm paper) */
  --wz-bg-2: #eef0e6;
  --wz-card: #ffffff;
  --wz-line: #e6e9dc;            /* hairline */
  --wz-line-2: #d5dac6;          /* stronger hairline */

  /* Effects */
  --wz-shadow-sm: 0 1px 2px rgba(18, 22, 15, 0.05);
  --wz-shadow-md: 0 1px 2px rgba(18, 22, 15, 0.04), 0 8px 24px rgba(18, 22, 15, 0.07);
  --wz-shadow-lg: 0 2px 4px rgba(18, 22, 15, 0.05), 0 16px 48px rgba(18, 22, 15, 0.12);

  /* Shape */
  --wz-r-lg: 14px;
  --wz-r-md: 10px;
  --wz-r-sm: 8px;

  /* Type */
  --wz-font-ui: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  --wz-font-display: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont,
    'Segoe UI', sans-serif;
  --wz-font-mono: ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, Consolas,
    monospace;
}

/* --------------------------------------------------------------------------
   Base
   -------------------------------------------------------------------------- */

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--wz-font-ui);
  background: var(--wz-bg);
  color: var(--wz-text);
  font-feature-settings: 'cv05' 1, 'cv11' 1; /* Inter: open digits/curves */
  text-rendering: optimizeLegibility;
}

::selection {
  background: var(--wz-green-glow);
  color: var(--wz-ink);
}

h1, h2, h3, h4, h5,
.ant-typography h1, .ant-typography h2, .ant-typography h3,
h1.ant-typography, h2.ant-typography, h3.ant-typography, h4.ant-typography {
  font-family: var(--wz-font-display);
  letter-spacing: -0.01em;
  color: var(--wz-ink);
}

/* Slim, quiet scrollbars everywhere */
*::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
*::-webkit-scrollbar-track {
  background: transparent;
}
*::-webkit-scrollbar-thumb {
  background: var(--wz-line-2);
  border-radius: 8px;
  border: 3px solid transparent;
  background-clip: padding-box;
}
*::-webkit-scrollbar-thumb:hover {
  background: var(--wz-green-strong);
  border: 3px solid transparent;
  background-clip: padding-box;
}

/* --------------------------------------------------------------------------
   Shared Wizzy pieces
   -------------------------------------------------------------------------- */

/* Wordmark: "Wizzy" + green dot */
.wz-wordmark {
  font-family: var(--wz-font-display);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--wz-ink);
  line-height: 1;
  display: inline-flex;
  align-items: baseline;
}

.wz-wordmark::after {
  content: '';
  width: 0.42em;
  height: 0.42em;
  margin-left: 0.14em;
  border-radius: 0.1em;
  background: linear-gradient(135deg, var(--wz-green) 0%, var(--wz-green-strong) 100%);
  transform: translateY(-0.04em);
}

/* Micro label: uppercase, tracked out — used for section headings */
.wz-label {
  font-family: var(--wz-font-ui);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--wz-text-2);
}

/* Signature keyline: thin green gradient line (top of headers) */
.wz-keyline {
  position: relative;
}
.wz-keyline::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg,
    var(--wz-green) 0%,
    var(--wz-green-strong) 38%,
    rgba(182, 206, 59, 0.12) 100%);
  z-index: 1;
  pointer-events: none;
}

/* Header user chip */
.wz-user-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 6px;
  border: 1px solid var(--wz-line);
  border-radius: 999px;
  background: var(--wz-card);
  color: var(--wz-text-2);
  font-size: 13px;
  font-weight: 500;
}

.wz-user-chip .wz-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--wz-green) 0%, #8fae21 100%);
  color: var(--wz-ink);
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-family: var(--wz-font-display);
}

/* Live pulse dot (Live-Suche) */
.wz-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--wz-green-strong);
  display: inline-block;
  position: relative;
  flex-shrink: 0;
}
.wz-live-dot::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1.5px solid var(--wz-green);
  opacity: 0;
  animation: wz-pulse 2s ease-out infinite;
}
.wz-live-dot.wz-live-off {
  background: var(--wz-line-2);
}
.wz-live-dot.wz-live-off::after {
  display: none;
}

@keyframes wz-pulse {
  0%   { transform: scale(0.6); opacity: 0.9; }
  70%  { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1.5); opacity: 0; }
}

/* Numbers read as data */
.wz-num,
.ant-table,
.ant-slider + div,
.ant-input-number-input {
  font-variant-numeric: tabular-nums lining-nums;
}

/* --------------------------------------------------------------------------
   Ant Design refinements (skin only)
   -------------------------------------------------------------------------- */

/* ---- Buttons ---- */
.ant-btn {
  font-weight: 500;
  border-radius: var(--wz-r-sm);
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: none;
}

.ant-btn-default {
  border-color: var(--wz-line-2);
  color: var(--wz-text);
}

.ant-btn-default:hover {
  border-color: var(--wz-green-strong) !important;
  color: var(--wz-green-deep) !important;
  box-shadow: var(--wz-shadow-sm);
}

.ant-btn-primary {
  background: var(--wz-green);
  border-color: var(--wz-green);
  color: var(--wz-ink);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 1px 2px rgba(18, 22, 15, 0.12);
}

.ant-btn-primary:not(:disabled):hover {
  background: var(--wz-green-strong) !important;
  border-color: var(--wz-green-strong) !important;
  color: var(--wz-ink) !important;
  transform: translateY(-1px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 6px 16px var(--wz-green-glow);
}

.ant-btn-primary:not(:disabled):active {
  transform: translateY(0);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 1px 2px rgba(18, 22, 15, 0.12);
}

.ant-btn-text:hover {
  background: var(--wz-bg-2) !important;
}

/* ---- Inputs & selects ---- */
.ant-input,
.ant-input-affix-wrapper,
.ant-select .ant-select-selector,
.ant-input-number {
  border-radius: var(--wz-r-sm);
}

.ant-input:hover,
.ant-input-affix-wrapper:hover,
.ant-select:not(.ant-select-disabled):hover .ant-select-selector,
.ant-input-number:hover {
  border-color: var(--wz-green-strong);
}

.ant-input:focus,
.ant-input-focused,
.ant-input-affix-wrapper:focus,
.ant-input-affix-wrapper-focused,
.ant-select-focused:not(.ant-select-disabled) .ant-select-selector,
.ant-input-number-focused {
  border-color: var(--wz-green-strong);
  box-shadow: 0 0 0 3px var(--wz-green-glow);
}

/* Multi-select chips */
.ant-select-multiple .ant-select-selection-item {
  background: var(--wz-green-tint);
  border: 1px solid var(--wz-green-tint-2);
  border-radius: 6px;
  font-weight: 500;
}

.ant-select-dropdown {
  border-radius: var(--wz-r-md);
  box-shadow: var(--wz-shadow-lg);
  border: 1px solid var(--wz-line);
  padding: 6px;
}

.ant-select-dropdown .ant-select-item {
  border-radius: 6px;
}

.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
  background: var(--wz-green-tint-2) !important;
  font-weight: 600;
}

.ant-select-item-option-active:not(.ant-select-item-option-disabled) {
  background: var(--wz-green-tint);
}

/* ---- Cards ---- */
.ant-card {
  border-radius: var(--wz-r-lg);
  border: 1px solid var(--wz-line);
  box-shadow: var(--wz-shadow-sm);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
  overflow: hidden;
}

.ant-card:hover {
  box-shadow: var(--wz-shadow-md);
}

.ant-card .ant-card-head {
  border-bottom: 1px solid var(--wz-line);
  background:
    linear-gradient(180deg, rgba(182, 206, 59, 0.045) 0%, rgba(182, 206, 59, 0) 100%),
    var(--wz-card);
  font-family: var(--wz-font-display);
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* ---- Tables ---- */
.ant-table-wrapper .ant-table {
  border-radius: var(--wz-r-md);
  font-variant-numeric: tabular-nums lining-nums;
}

.ant-table-wrapper .ant-table-thead > tr > th {
  background: #f4f6ec !important;
  color: var(--wz-text-2) !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--wz-line-2) !important;
}

.ant-table-wrapper .ant-table-tbody > tr > td {
  border-bottom: 1px solid var(--wz-line);
  transition: background 0.15s ease;
}

.ant-table-wrapper .ant-table-tbody > tr:hover > td {
  background: var(--wz-green-tint) !important;
}

/* ---- Tags ---- */
.ant-tag {
  border-radius: 6px;
  font-weight: 500;
  padding-inline: 8px;
}

/* ---- Slider ---- */
.ant-slider .ant-slider-rail {
  background: var(--wz-bg-2);
}
.ant-slider .ant-slider-track {
  background: linear-gradient(90deg, var(--wz-green-strong), var(--wz-green)) !important;
}
.ant-slider .ant-slider-handle::after {
  box-shadow: 0 0 0 2.5px var(--wz-green-strong), var(--wz-shadow-sm) !important;
}
.ant-slider:hover .ant-slider-handle::after {
  box-shadow: 0 0 0 3px var(--wz-green-deep), var(--wz-shadow-sm) !important;
}

/* ---- Switch ---- */
.ant-switch.ant-switch-checked {
  background: var(--wz-green-strong) !important;
}
.ant-switch.ant-switch-checked .ant-switch-inner {
  color: var(--wz-ink);
  font-weight: 600;
}

/* ---- Divider ---- */
.ant-divider {
  border-color: var(--wz-line);
}

/* ---- Modal / Message / Tooltip ---- */
.ant-modal .ant-modal-content {
  border-radius: var(--wz-r-lg);
  box-shadow: var(--wz-shadow-lg);
}

.ant-message .ant-message-notice-content {
  border-radius: 999px;
  box-shadow: var(--wz-shadow-md);
  border: 1px solid var(--wz-line);
  padding: 9px 18px;
  font-weight: 500;
}

.ant-tooltip .ant-tooltip-inner {
  border-radius: var(--wz-r-sm);
  background: var(--wz-ink-2);
  font-size: 12px;
}
.ant-tooltip .ant-tooltip-arrow::before,
.ant-tooltip .ant-tooltip-arrow::after {
  background: var(--wz-ink-2);
}

/* ---- Spin ---- */
.ant-spin .ant-spin-dot-item {
  background-color: var(--wz-green-strong);
}

/* ---- Empty ---- */
.ant-empty-description {
  color: var(--wz-text-3);
}

/* ---- Alert ---- */
.ant-alert {
  border-radius: var(--wz-r-md);
}

/* ---- Focus visibility (keyboard) ---- */
.ant-btn:focus-visible,
.ant-tag:focus-visible {
  outline: 2px solid var(--wz-green-strong);
  outline-offset: 2px;
}
`, "",{"version":3,"sources":["webpack://./src/theme.css"],"names":[],"mappings":"AAAA;;;+EAG+E;;AAE/E;EACE,UAAU;EACV,mBAAmB;EACnB,0BAA0B;EAC1B,wBAAwB,OAAO,qCAAqC;EACpE,wBAAwB,OAAO,sBAAsB;EACrD,0BAA0B,KAAK,kCAAkC;EACjE,yCAAyC;;EAEzC,eAAe;EACf,iBAAiB,cAAc,oCAAoC;EACnE,mBAAmB;EACnB,kBAAkB;EAClB,oBAAoB;EACpB,oBAAoB;;EAEpB,aAAa;EACb,gBAAgB,eAAe,gCAAgC;EAC/D,kBAAkB;EAClB,kBAAkB;EAClB,kBAAkB,aAAa,aAAa;EAC5C,oBAAoB,WAAW,sBAAsB;;EAErD,YAAY;EACZ,gDAAgD;EAChD,mFAAmF;EACnF,oFAAoF;;EAEpF,UAAU;EACV,eAAe;EACf,eAAe;EACf,cAAc;;EAEd,SAAS;EACT;uCACqC;EACrC;0BACwB;EACxB;aACW;AACb;;AAEA;;+EAE+E;;AAE/E;EACE,uBAAuB;AACzB;;AAEA;EACE,8BAA8B;EAC9B,wBAAwB;EACxB,qBAAqB;EACrB,yCAAyC,EAAE,8BAA8B;EACzE,kCAAkC;AACpC;;AAEA;EACE,gCAAgC;EAChC,oBAAoB;AACtB;;AAEA;;;EAGE,mCAAmC;EACnC,uBAAuB;EACvB,oBAAoB;AACtB;;AAEA,sCAAsC;AACtC;EACE,WAAW;EACX,YAAY;AACd;AACA;EACE,uBAAuB;AACzB;AACA;EACE,4BAA4B;EAC5B,kBAAkB;EAClB,6BAA6B;EAC7B,4BAA4B;AAC9B;AACA;EACE,kCAAkC;EAClC,6BAA6B;EAC7B,4BAA4B;AAC9B;;AAEA;;+EAE+E;;AAE/E,kCAAkC;AAClC;EACE,mCAAmC;EACnC,gBAAgB;EAChB,uBAAuB;EACvB,oBAAoB;EACpB,cAAc;EACd,oBAAoB;EACpB,qBAAqB;AACvB;;AAEA;EACE,WAAW;EACX,aAAa;EACb,cAAc;EACd,mBAAmB;EACnB,oBAAoB;EACpB,oFAAoF;EACpF,8BAA8B;AAChC;;AAEA,oEAAoE;AACpE;EACE,8BAA8B;EAC9B,eAAe;EACf,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,uBAAuB;AACzB;;AAEA,iEAAiE;AACjE;EACE,kBAAkB;AACpB;AACA;EACE,WAAW;EACX,kBAAkB;EAClB,MAAM;EACN,OAAO;EACP,QAAQ;EACR,WAAW;EACX;;;kCAGgC;EAChC,UAAU;EACV,oBAAoB;AACtB;;AAEA,qBAAqB;AACrB;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,yBAAyB;EACzB,gCAAgC;EAChC,oBAAoB;EACpB,0BAA0B;EAC1B,uBAAuB;EACvB,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,qEAAqE;EACrE,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,yBAAyB;EACzB,mCAAmC;AACrC;;AAEA,gCAAgC;AAChC;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,kCAAkC;EAClC,qBAAqB;EACrB,kBAAkB;EAClB,cAAc;AAChB;AACA;EACE,WAAW;EACX,kBAAkB;EAClB,WAAW;EACX,kBAAkB;EAClB,mCAAmC;EACnC,UAAU;EACV,wCAAwC;AAC1C;AACA;EACE,4BAA4B;AAC9B;AACA;EACE,aAAa;AACf;;AAEA;EACE,OAAO,qBAAqB,EAAE,YAAY,EAAE;EAC5C,OAAO,qBAAqB,EAAE,UAAU,EAAE;EAC1C,OAAO,qBAAqB,EAAE,UAAU,EAAE;AAC5C;;AAEA,yBAAyB;AACzB;;;;EAIE,8CAA8C;AAChD;;AAEA;;+EAE+E;;AAE/E,sBAAsB;AACtB;EACE,gBAAgB;EAChB,6BAA6B;EAC7B,kDAAkD;EAClD,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;EAC9B,qBAAqB;AACvB;;AAEA;EACE,+CAA+C;EAC/C,sCAAsC;EACtC,+BAA+B;AACjC;;AAEA;EACE,2BAA2B;EAC3B,6BAA6B;EAC7B,oBAAoB;EACpB,qFAAqF;AACvF;;AAEA;EACE,6CAA6C;EAC7C,+CAA+C;EAC/C,+BAA+B;EAC/B,2BAA2B;EAC3B,oFAAoF;AACtF;;AAEA;EACE,wBAAwB;EACxB,qFAAqF;AACvF;;AAEA;EACE,qCAAqC;AACvC;;AAEA,+BAA+B;AAC/B;;;;EAIE,6BAA6B;AAC/B;;AAEA;;;;EAIE,oCAAoC;AACtC;;AAEA;;;;;;EAME,oCAAoC;EACpC,0CAA0C;AAC5C;;AAEA,uBAAuB;AACvB;EACE,gCAAgC;EAChC,wCAAwC;EACxC,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,6BAA6B;EAC7B,+BAA+B;EAC/B,gCAAgC;EAChC,YAAY;AACd;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,6CAA6C;EAC7C,gBAAgB;AAClB;;AAEA;EACE,gCAAgC;AAClC;;AAEA,oBAAoB;AACpB;EACE,6BAA6B;EAC7B,gCAAgC;EAChC,+BAA+B;EAC/B,0DAA0D;EAC1D,gBAAgB;AAClB;;AAEA;EACE,+BAA+B;AACjC;;AAEA;EACE,uCAAuC;EACvC;;kBAEgB;EAChB,mCAAmC;EACnC,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA,qBAAqB;AACrB;EACE,6BAA6B;EAC7B,8CAA8C;AAChD;;AAEA;EACE,8BAA8B;EAC9B,kCAAkC;EAClC,0BAA0B;EAC1B,2BAA2B;EAC3B,sBAAsB;EACtB,yBAAyB;EACzB,oDAAoD;AACtD;;AAEA;EACE,uCAAuC;EACvC,iCAAiC;AACnC;;AAEA;EACE,2CAA2C;AAC7C;;AAEA,mBAAmB;AACnB;EACE,kBAAkB;EAClB,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA,qBAAqB;AACrB;EACE,0BAA0B;AAC5B;AACA;EACE,sFAAsF;AACxF;AACA;EACE,8EAA8E;AAChF;AACA;EACE,0EAA0E;AAC5E;;AAEA,qBAAqB;AACrB;EACE,6CAA6C;AAC/C;AACA;EACE,oBAAoB;EACpB,gBAAgB;AAClB;;AAEA,sBAAsB;AACtB;EACE,4BAA4B;AAC9B;;AAEA,wCAAwC;AACxC;EACE,6BAA6B;EAC7B,+BAA+B;AACjC;;AAEA;EACE,oBAAoB;EACpB,+BAA+B;EAC/B,gCAAgC;EAChC,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE,6BAA6B;EAC7B,2BAA2B;EAC3B,eAAe;AACjB;AACA;;EAEE,2BAA2B;AAC7B;;AAEA,mBAAmB;AACnB;EACE,wCAAwC;AAC1C;;AAEA,oBAAoB;AACpB;EACE,uBAAuB;AACzB;;AAEA,oBAAoB;AACpB;EACE,6BAA6B;AAC/B;;AAEA,0CAA0C;AAC1C;;EAEE,yCAAyC;EACzC,mBAAmB;AACrB","sourcesContent":["/* ==========================================================================\n   WIZZY design system — premium light theme with ink + brand green\n   Purely visual: no layout/behavioral overrides beyond spacing & skin.\n   ========================================================================== */\n\n:root {\n  /* Brand */\n  --wz-green: #b6ce3b;\n  --wz-green-strong: #a4bd2d;\n  --wz-green-deep: #5f7d16;      /* readable green for text on light */\n  --wz-green-tint: #f3f7e2;      /* subtle green wash */\n  --wz-green-tint-2: #e9f0cd;    /* stronger wash (active states) */\n  --wz-green-glow: rgba(182, 206, 59, 0.32);\n\n  /* Ink & text */\n  --wz-ink: #12160f;             /* near-black with green undertone */\n  --wz-ink-2: #232920;\n  --wz-text: #1c211a;\n  --wz-text-2: #5d6457;\n  --wz-text-3: #8f968a;\n\n  /* Surfaces */\n  --wz-bg: #f6f7f1;              /* app background (warm paper) */\n  --wz-bg-2: #eef0e6;\n  --wz-card: #ffffff;\n  --wz-line: #e6e9dc;            /* hairline */\n  --wz-line-2: #d5dac6;          /* stronger hairline */\n\n  /* Effects */\n  --wz-shadow-sm: 0 1px 2px rgba(18, 22, 15, 0.05);\n  --wz-shadow-md: 0 1px 2px rgba(18, 22, 15, 0.04), 0 8px 24px rgba(18, 22, 15, 0.07);\n  --wz-shadow-lg: 0 2px 4px rgba(18, 22, 15, 0.05), 0 16px 48px rgba(18, 22, 15, 0.12);\n\n  /* Shape */\n  --wz-r-lg: 14px;\n  --wz-r-md: 10px;\n  --wz-r-sm: 8px;\n\n  /* Type */\n  --wz-font-ui: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,\n    'Helvetica Neue', Arial, sans-serif;\n  --wz-font-display: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont,\n    'Segoe UI', sans-serif;\n  --wz-font-mono: ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, Consolas,\n    monospace;\n}\n\n/* --------------------------------------------------------------------------\n   Base\n   -------------------------------------------------------------------------- */\n\nhtml {\n  scroll-behavior: smooth;\n}\n\nbody {\n  font-family: var(--wz-font-ui);\n  background: var(--wz-bg);\n  color: var(--wz-text);\n  font-feature-settings: 'cv05' 1, 'cv11' 1; /* Inter: open digits/curves */\n  text-rendering: optimizeLegibility;\n}\n\n::selection {\n  background: var(--wz-green-glow);\n  color: var(--wz-ink);\n}\n\nh1, h2, h3, h4, h5,\n.ant-typography h1, .ant-typography h2, .ant-typography h3,\nh1.ant-typography, h2.ant-typography, h3.ant-typography, h4.ant-typography {\n  font-family: var(--wz-font-display);\n  letter-spacing: -0.01em;\n  color: var(--wz-ink);\n}\n\n/* Slim, quiet scrollbars everywhere */\n*::-webkit-scrollbar {\n  width: 10px;\n  height: 10px;\n}\n*::-webkit-scrollbar-track {\n  background: transparent;\n}\n*::-webkit-scrollbar-thumb {\n  background: var(--wz-line-2);\n  border-radius: 8px;\n  border: 3px solid transparent;\n  background-clip: padding-box;\n}\n*::-webkit-scrollbar-thumb:hover {\n  background: var(--wz-green-strong);\n  border: 3px solid transparent;\n  background-clip: padding-box;\n}\n\n/* --------------------------------------------------------------------------\n   Shared Wizzy pieces\n   -------------------------------------------------------------------------- */\n\n/* Wordmark: \"Wizzy\" + green dot */\n.wz-wordmark {\n  font-family: var(--wz-font-display);\n  font-weight: 700;\n  letter-spacing: -0.02em;\n  color: var(--wz-ink);\n  line-height: 1;\n  display: inline-flex;\n  align-items: baseline;\n}\n\n.wz-wordmark::after {\n  content: '';\n  width: 0.42em;\n  height: 0.42em;\n  margin-left: 0.14em;\n  border-radius: 0.1em;\n  background: linear-gradient(135deg, var(--wz-green) 0%, var(--wz-green-strong) 100%);\n  transform: translateY(-0.04em);\n}\n\n/* Micro label: uppercase, tracked out — used for section headings */\n.wz-label {\n  font-family: var(--wz-font-ui);\n  font-size: 11px;\n  font-weight: 600;\n  letter-spacing: 0.09em;\n  text-transform: uppercase;\n  color: var(--wz-text-2);\n}\n\n/* Signature keyline: thin green gradient line (top of headers) */\n.wz-keyline {\n  position: relative;\n}\n.wz-keyline::before {\n  content: '';\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  height: 2px;\n  background: linear-gradient(90deg,\n    var(--wz-green) 0%,\n    var(--wz-green-strong) 38%,\n    rgba(182, 206, 59, 0.12) 100%);\n  z-index: 1;\n  pointer-events: none;\n}\n\n/* Header user chip */\n.wz-user-chip {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  padding: 5px 12px 5px 6px;\n  border: 1px solid var(--wz-line);\n  border-radius: 999px;\n  background: var(--wz-card);\n  color: var(--wz-text-2);\n  font-size: 13px;\n  font-weight: 500;\n}\n\n.wz-user-chip .wz-avatar {\n  width: 24px;\n  height: 24px;\n  border-radius: 50%;\n  background: linear-gradient(135deg, var(--wz-green) 0%, #8fae21 100%);\n  color: var(--wz-ink);\n  font-size: 11px;\n  font-weight: 700;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  text-transform: uppercase;\n  font-family: var(--wz-font-display);\n}\n\n/* Live pulse dot (Live-Suche) */\n.wz-live-dot {\n  width: 8px;\n  height: 8px;\n  border-radius: 50%;\n  background: var(--wz-green-strong);\n  display: inline-block;\n  position: relative;\n  flex-shrink: 0;\n}\n.wz-live-dot::after {\n  content: '';\n  position: absolute;\n  inset: -4px;\n  border-radius: 50%;\n  border: 1.5px solid var(--wz-green);\n  opacity: 0;\n  animation: wz-pulse 2s ease-out infinite;\n}\n.wz-live-dot.wz-live-off {\n  background: var(--wz-line-2);\n}\n.wz-live-dot.wz-live-off::after {\n  display: none;\n}\n\n@keyframes wz-pulse {\n  0%   { transform: scale(0.6); opacity: 0.9; }\n  70%  { transform: scale(1.5); opacity: 0; }\n  100% { transform: scale(1.5); opacity: 0; }\n}\n\n/* Numbers read as data */\n.wz-num,\n.ant-table,\n.ant-slider + div,\n.ant-input-number-input {\n  font-variant-numeric: tabular-nums lining-nums;\n}\n\n/* --------------------------------------------------------------------------\n   Ant Design refinements (skin only)\n   -------------------------------------------------------------------------- */\n\n/* ---- Buttons ---- */\n.ant-btn {\n  font-weight: 500;\n  border-radius: var(--wz-r-sm);\n  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);\n  box-shadow: none;\n}\n\n.ant-btn-default {\n  border-color: var(--wz-line-2);\n  color: var(--wz-text);\n}\n\n.ant-btn-default:hover {\n  border-color: var(--wz-green-strong) !important;\n  color: var(--wz-green-deep) !important;\n  box-shadow: var(--wz-shadow-sm);\n}\n\n.ant-btn-primary {\n  background: var(--wz-green);\n  border-color: var(--wz-green);\n  color: var(--wz-ink);\n  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 1px 2px rgba(18, 22, 15, 0.12);\n}\n\n.ant-btn-primary:not(:disabled):hover {\n  background: var(--wz-green-strong) !important;\n  border-color: var(--wz-green-strong) !important;\n  color: var(--wz-ink) !important;\n  transform: translateY(-1px);\n  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 6px 16px var(--wz-green-glow);\n}\n\n.ant-btn-primary:not(:disabled):active {\n  transform: translateY(0);\n  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 1px 2px rgba(18, 22, 15, 0.12);\n}\n\n.ant-btn-text:hover {\n  background: var(--wz-bg-2) !important;\n}\n\n/* ---- Inputs & selects ---- */\n.ant-input,\n.ant-input-affix-wrapper,\n.ant-select .ant-select-selector,\n.ant-input-number {\n  border-radius: var(--wz-r-sm);\n}\n\n.ant-input:hover,\n.ant-input-affix-wrapper:hover,\n.ant-select:not(.ant-select-disabled):hover .ant-select-selector,\n.ant-input-number:hover {\n  border-color: var(--wz-green-strong);\n}\n\n.ant-input:focus,\n.ant-input-focused,\n.ant-input-affix-wrapper:focus,\n.ant-input-affix-wrapper-focused,\n.ant-select-focused:not(.ant-select-disabled) .ant-select-selector,\n.ant-input-number-focused {\n  border-color: var(--wz-green-strong);\n  box-shadow: 0 0 0 3px var(--wz-green-glow);\n}\n\n/* Multi-select chips */\n.ant-select-multiple .ant-select-selection-item {\n  background: var(--wz-green-tint);\n  border: 1px solid var(--wz-green-tint-2);\n  border-radius: 6px;\n  font-weight: 500;\n}\n\n.ant-select-dropdown {\n  border-radius: var(--wz-r-md);\n  box-shadow: var(--wz-shadow-lg);\n  border: 1px solid var(--wz-line);\n  padding: 6px;\n}\n\n.ant-select-dropdown .ant-select-item {\n  border-radius: 6px;\n}\n\n.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {\n  background: var(--wz-green-tint-2) !important;\n  font-weight: 600;\n}\n\n.ant-select-item-option-active:not(.ant-select-item-option-disabled) {\n  background: var(--wz-green-tint);\n}\n\n/* ---- Cards ---- */\n.ant-card {\n  border-radius: var(--wz-r-lg);\n  border: 1px solid var(--wz-line);\n  box-shadow: var(--wz-shadow-sm);\n  transition: box-shadow 0.25s ease, border-color 0.25s ease;\n  overflow: hidden;\n}\n\n.ant-card:hover {\n  box-shadow: var(--wz-shadow-md);\n}\n\n.ant-card .ant-card-head {\n  border-bottom: 1px solid var(--wz-line);\n  background:\n    linear-gradient(180deg, rgba(182, 206, 59, 0.045) 0%, rgba(182, 206, 59, 0) 100%),\n    var(--wz-card);\n  font-family: var(--wz-font-display);\n  font-weight: 600;\n  letter-spacing: -0.01em;\n}\n\n/* ---- Tables ---- */\n.ant-table-wrapper .ant-table {\n  border-radius: var(--wz-r-md);\n  font-variant-numeric: tabular-nums lining-nums;\n}\n\n.ant-table-wrapper .ant-table-thead > tr > th {\n  background: #f4f6ec !important;\n  color: var(--wz-text-2) !important;\n  font-size: 11px !important;\n  font-weight: 600 !important;\n  letter-spacing: 0.07em;\n  text-transform: uppercase;\n  border-bottom: 1px solid var(--wz-line-2) !important;\n}\n\n.ant-table-wrapper .ant-table-tbody > tr > td {\n  border-bottom: 1px solid var(--wz-line);\n  transition: background 0.15s ease;\n}\n\n.ant-table-wrapper .ant-table-tbody > tr:hover > td {\n  background: var(--wz-green-tint) !important;\n}\n\n/* ---- Tags ---- */\n.ant-tag {\n  border-radius: 6px;\n  font-weight: 500;\n  padding-inline: 8px;\n}\n\n/* ---- Slider ---- */\n.ant-slider .ant-slider-rail {\n  background: var(--wz-bg-2);\n}\n.ant-slider .ant-slider-track {\n  background: linear-gradient(90deg, var(--wz-green-strong), var(--wz-green)) !important;\n}\n.ant-slider .ant-slider-handle::after {\n  box-shadow: 0 0 0 2.5px var(--wz-green-strong), var(--wz-shadow-sm) !important;\n}\n.ant-slider:hover .ant-slider-handle::after {\n  box-shadow: 0 0 0 3px var(--wz-green-deep), var(--wz-shadow-sm) !important;\n}\n\n/* ---- Switch ---- */\n.ant-switch.ant-switch-checked {\n  background: var(--wz-green-strong) !important;\n}\n.ant-switch.ant-switch-checked .ant-switch-inner {\n  color: var(--wz-ink);\n  font-weight: 600;\n}\n\n/* ---- Divider ---- */\n.ant-divider {\n  border-color: var(--wz-line);\n}\n\n/* ---- Modal / Message / Tooltip ---- */\n.ant-modal .ant-modal-content {\n  border-radius: var(--wz-r-lg);\n  box-shadow: var(--wz-shadow-lg);\n}\n\n.ant-message .ant-message-notice-content {\n  border-radius: 999px;\n  box-shadow: var(--wz-shadow-md);\n  border: 1px solid var(--wz-line);\n  padding: 9px 18px;\n  font-weight: 500;\n}\n\n.ant-tooltip .ant-tooltip-inner {\n  border-radius: var(--wz-r-sm);\n  background: var(--wz-ink-2);\n  font-size: 12px;\n}\n.ant-tooltip .ant-tooltip-arrow::before,\n.ant-tooltip .ant-tooltip-arrow::after {\n  background: var(--wz-ink-2);\n}\n\n/* ---- Spin ---- */\n.ant-spin .ant-spin-dot-item {\n  background-color: var(--wz-green-strong);\n}\n\n/* ---- Empty ---- */\n.ant-empty-description {\n  color: var(--wz-text-3);\n}\n\n/* ---- Alert ---- */\n.ant-alert {\n  border-radius: var(--wz-r-md);\n}\n\n/* ---- Focus visibility (keyboard) ---- */\n.ant-btn:focus-visible,\n.ant-tag:focus-visible {\n  outline: 2px solid var(--wz-green-strong);\n  outline-offset: 2px;\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
